/**
 * Semáforo único del Centro de envíos: fusiona el semáforo de ENVÍO (validación de datos para la
 * etiqueta, ya existente) con el de FACTURA (semáforo pre-factura del backend, /api/prefactura).
 *
 * - Color final = el PEOR de los dos (factura rojo → rojo; ámbar o "en espera" → ámbar).
 * - Si la parte de factura no está disponible (sin permiso, error o cargando) la bandera es
 *   exactamente la de siempre: solo envío.
 * - Clic en la bandera → modal con el detalle PrestaShop vs Factusol y "Marcar revisado".
 * - Comentario del cliente y observaciones de su ficha: ámbar hasta que alguien los lee y decide
 *   en qué estado deja el pedido (verde, o ámbar si queda algo por solucionar). Lector rápido para
 *   leerlos todos seguidos (MessagesReaderModal).
 */
import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  X, Loader2, CheckCircle2, AlertTriangle, XCircle, Clock, Info, Undo2, MessageSquareText, StickyNote,
  ChevronLeft, ChevronRight, Check, PartyPopper,
} from 'lucide-react'
import { prefactura, PrefacturaStatus, PrefacturaDetail, PrefacturaMessageData, StockDecision } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

export type FlagStatus = 'green' | 'yellow' | 'red'

export interface ShippingValidation {
  status: FlagStatus
  issues: string[]
}

const RANK: Record<FlagStatus, number> = { green: 0, yellow: 1, red: 2 }

/** Color de la bandera aportado por la parte de factura (null = no altera la de envío). */
export function invoiceFlag(inv?: PrefacturaStatus | null): FlagStatus | null {
  if (!inv || inv.kind !== 'eval' || !inv.color) return null
  if (inv.color === 'red') return 'red'
  if (inv.color === 'amber' || inv.color === 'grey') return 'yellow'
  return 'green'
}

/** Peor de envío y factura. */
export function combineStatus(ship: ShippingValidation, inv?: PrefacturaStatus | null): FlagStatus {
  const f = invoiceFlag(inv)
  if (!f) return ship.status
  return RANK[f] > RANK[ship.status] ? f : ship.status
}

/** ¿Listo para facturar? (solo la parte de factura, independiente de los avisos de envío) */
export function isReadyToInvoice(inv?: PrefacturaStatus | null): boolean {
  return !!inv && inv.kind === 'eval' && inv.color === 'green'
}

const FLAG_BG: Record<FlagStatus, string> = {
  green: 'bg-green-500',
  yellow: 'bg-amber-500',
  red: 'bg-red-500',
}

const SEV_ICON = {
  red: <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />,
  grey: <Clock className="w-3.5 h-3.5 text-slate-300 shrink-0" />,
  amber: <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />,
  info: <Info className="w-3.5 h-3.5 text-sky-300 shrink-0" />,
}

function invoiceTooltipLines(inv: PrefacturaStatus | null | undefined, loading: boolean, failed: boolean) {
  if (loading) return <span className="text-slate-300">Calculando…</span>
  if (failed) return <span className="text-slate-300">No disponible (Factusol no responde)</span>
  if (!inv) return <span className="text-slate-300">Sin datos</span>
  if (inv.kind === 'facturado') return <span className="text-slate-300">✓ Facturado o albaranado en Factusol</span>
  if (inv.kind === 'anulado') return <span className="text-slate-300">Pedido anulado en Factusol</span>
  if (inv.kind !== 'eval') return <span className="text-slate-300">{inv.summary || 'Sin evaluar'}</span>
  if (inv.color === 'green') {
    return <span className="text-green-300">✓ {inv.reviewed ? 'Revisado: listo para facturar' : 'Listo para facturar'}</span>
  }
  const relevant = (inv.issues || []).filter(i => i.severity !== 'info').slice(0, 5)
  return (
    <ul className="space-y-0.5">
      {relevant.map((i, k) => (
        <li key={k} className="flex items-start gap-1">{SEV_ICON[i.severity]}<span>{i.msg}</span></li>
      ))}
      <li className="text-slate-400 pt-0.5">Clic en la bandera para ver el detalle</li>
    </ul>
  )
}

/**
 * Tooltip de la bandera pintado en <body> (portal, posición fija): la tabla de pedidos tiene
 * overflow-hidden y, con pocos pedidos, un tooltip absoluto quedaba recortado e ilegible.
 * Se abre debajo alineado a la derecha; si no cabe, encima; nunca se sale de la ventana.
 */
export function FlagTooltip({ content, children, className = '' }: { content: ReactNode; children: ReactNode; className?: string }) {
  const anchorRef = useRef<HTMLDivElement>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  useLayoutEffect(() => {
    if (!open || !anchorRef.current || !tipRef.current) return
    const a = anchorRef.current.getBoundingClientRect()
    const t = tipRef.current.getBoundingClientRect()
    const gap = 4, margin = 8
    let top = a.bottom + gap
    if (top + t.height > window.innerHeight - margin && a.top - gap - t.height >= margin) {
      top = a.top - gap - t.height
    }
    top = Math.max(margin, Math.min(top, window.innerHeight - t.height - margin))
    const left = Math.max(margin, Math.min(a.right - t.width, window.innerWidth - t.width - margin))
    setPos({ top, left })
  }, [open])

  return (
    <div
      ref={anchorRef}
      className={className}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => { setOpen(false); setPos(null) }}
    >
      {children}
      {open && createPortal(
        <div
          ref={tipRef}
          className="fixed z-[1000] pointer-events-none"
          style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' }}
        >
          {content}
        </div>,
        document.body
      )}
    </div>
  )
}

interface CombinedFlagProps {
  shipping: ShippingValidation
  invoice?: PrefacturaStatus | null
  invoiceLoading?: boolean
  invoiceFailed?: boolean
  onOpenInvoice?: () => void
}

/** Bandera única Envío + Factura (mismo aspecto que la bandera de envío de siempre). */
export function CombinedFlag({ shipping, invoice, invoiceLoading = false, invoiceFailed = false, onOpenInvoice }: CombinedFlagProps) {
  const status = combineStatus(shipping, invoice)
  const canOpen = !!onOpenInvoice && !!invoice && invoice.kind === 'eval'
  return (
    <div
      className={canOpen ? 'cursor-pointer' : ''}
      onClick={canOpen ? (e) => { e.stopPropagation(); onOpenInvoice!() } : undefined}
    >
      <FlagTooltip
        className="relative flex items-center justify-center"
        content={
        <div className="bg-slate-900 text-white text-xs rounded-lg p-2 shadow-lg w-80 space-y-2">
          <div>
            <p className="font-medium mb-1">Envío</p>
            {shipping.issues.length === 0 ? (
              <span className="text-green-300">✓ Datos correctos</span>
            ) : (
              <ul className="space-y-0.5">
                {shipping.issues.map((issue, i) => (
                  <li key={i} className="flex items-center gap-1">
                    <span className="w-1 h-1 bg-current rounded-full" />
                    {issue}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border-t border-slate-700 pt-1.5">
            <p className="font-medium mb-1">Factura</p>
            {invoiceTooltipLines(invoice, invoiceLoading, invoiceFailed)}
          </div>
        </div>
        }
      >
        <div className={`w-4 h-5 ${FLAG_BG[status]} rounded-sm relative`}>
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[4px] border-l-transparent border-r-transparent border-b-white dark:border-b-slate-800" />
        </div>
      </FlagTooltip>
    </div>
  )
}

// ---------------------------------------------------------------------------------------------
// Comentario del cliente y observaciones de su ficha
// ---------------------------------------------------------------------------------------------

const MESSAGE_CODES = ['comentario_cliente', 'observaciones_cliente']
type MsgIssue = { code: string; severity: string; data?: PrefacturaMessageData | Record<string, any> }

export const isMessageIssue = (i: { code: string }) => MESSAGE_CODES.includes(i.code)

/** Avisos de comentario/observaciones aún sin leer de un pedido. */
export function unreadMessages(inv?: PrefacturaStatus | null) {
  if (!inv || inv.kind !== 'eval') return []
  return (inv.issues || []).filter(i => isMessageIssue(i) && i.severity === 'amber' && !i.data?.read)
}

type Decision = 'ok' | 'pendiente'

/** Banderita con el mismo aspecto que la de la lista de pedidos. */
function MiniFlag({ color, className = '' }: { color: 'green' | 'amber'; className?: string }) {
  return (
    <span className={`relative inline-block w-4 h-5 rounded-sm ${color === 'green' ? 'bg-green-500' : 'bg-amber-500'} ${className}`}>
      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[4px] border-l-transparent border-r-transparent border-b-white dark:border-b-slate-800" />
    </span>
  )
}

/**
 * Pregunta tras leer: ¿en qué estado dejamos el pedido? Verde (todo correcto) o ámbar (queda algo
 * pendiente: al abrir la bandera más tarde se ven el comentario y las observaciones para resolverlo).
 * `keys` activa los atajos V / A.
 */
export function DecisionPicker({ onDecide, pending, keys = false }: {
  onDecide: (d: Decision) => void
  pending: boolean
  keys?: boolean
}) {
  useEffect(() => {
    if (!keys) return
    const onKey = (e: KeyboardEvent) => {
      if (pending || (e.target as HTMLElement)?.tagName === 'INPUT') return
      const k = e.key.toLowerCase()
      if (k === 'v' || k === '1') { e.preventDefault(); onDecide('ok') }
      else if (k === 'a' || k === '2') { e.preventDefault(); onDecide('pendiente') }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [keys, pending, onDecide])

  const opt = (d: Decision) => {
    const green = d === 'ok'
    return (
      <button
        type="button"
        onClick={() => onDecide(d)}
        disabled={pending}
        className={`flex items-center gap-3 rounded-xl border-2 px-3.5 py-3 text-left disabled:opacity-50 transition-colors ${green
          ? 'border-green-200 dark:border-green-900/60 bg-green-50/60 dark:bg-green-950/20 hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-950/40'
          : 'border-amber-200 dark:border-amber-900/60 bg-amber-50/60 dark:bg-amber-950/20 hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950/40'}`}
      >
        <MiniFlag color={green ? 'green' : 'amber'} className="shrink-0" />
        <span className="flex-1">
          <span className={`block text-sm font-semibold ${green ? 'text-green-800 dark:text-green-300' : 'text-amber-800 dark:text-amber-300'}`}>
            {green ? 'Verde' : 'Ámbar'}
          </span>
          <span className={`block text-xs ${green ? 'text-green-700/80 dark:text-green-400/80' : 'text-amber-700/80 dark:text-amber-400/80'}`}>
            {green ? 'Leído, todo correcto' : 'Queda pendiente de solucionar'}
          </span>
        </span>
        {keys && (
          <kbd className={`hidden sm:inline px-1.5 py-0.5 rounded border text-[10px] font-mono ${green
            ? 'border-green-300 dark:border-green-800 text-green-700 dark:text-green-400'
            : 'border-amber-300 dark:border-amber-800 text-amber-700 dark:text-amber-400'}`}>{green ? 'V' : 'A'}</kbd>
        )}
      </button>
    )
  }

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3.5">
      <p className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2.5">
        ¿En qué estado dejamos el pedido?
        {pending && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      </p>
      <div className="grid grid-cols-2 gap-2.5">
        {opt('ok')}
        {opt('pendiente')}
      </div>
    </div>
  )
}

const fmtDateTime = (s?: string) => {
  if (!s) return ''
  const t = s.replace('T', ' ')
  return `${t.slice(8, 10)}/${t.slice(5, 7)}/${t.slice(0, 4)} ${t.slice(11, 16)}`
}

function ReadChip({ issue }: { issue: MsgIssue }) {
  const rd = issue.data?.read as PrefacturaMessageData['read']
  if (!rd) {
    return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-500 text-white">Sin leer</span>
  }
  if (rd.decision === 'pendiente') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
        Pendiente · {rd.read_by} · {fmtDateTime(rd.read_at)}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
      <Check className="w-3 h-3" /> Leído{rd ? ` por ${rd.read_by} · ${fmtDateTime(rd.read_at)}` : ''}
    </span>
  )
}

/** Observaciones de la ficha tal cual están escritas; resalta (solo visualmente) las líneas que citan este pedido. */
function NoteText({ note, orderId }: { note: string; orderId: number }) {
  const id = String(orderId)
  const lines = note.split('\n')
  return (
    <div className="max-h-72 overflow-y-auto rounded-lg bg-white dark:bg-slate-900/60 border border-violet-100 dark:border-violet-900/40 divide-y divide-violet-50 dark:divide-slate-800">
      {lines.map((l, k) => {
        const mine = l.includes(id)
        return (
          <p key={k} className={`px-3 py-1.5 text-sm leading-relaxed whitespace-pre-wrap break-words ${
            mine ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-950 dark:text-amber-100 font-medium' : 'text-slate-700 dark:text-slate-200'
          }`}>
            {mine && <span className="mr-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-500 text-white align-middle">este pedido</span>}
            {l || ' '}
          </p>
        )
      })}
    </div>
  )
}

/** Bloque de lectura: comentario(s) del cliente y observaciones de su ficha. */
export function CustomerMessages({ orderId, issues }: { orderId: number; issues: MsgIssue[] }) {
  const comment = issues.find(i => i.code === 'comentario_cliente')
  const note = issues.find(i => i.code === 'observaciones_cliente')
  if (!comment && !note) return null
  const msgs = (comment?.data?.messages || []) as { text: string; date: string }[]
  const pend = ([comment, note].find(i => i?.data?.read?.decision === 'pendiente')?.data?.read) as PrefacturaMessageData['read']
  return (
    <div className="space-y-3">
      {pend && (
        <div className="flex items-start gap-2.5 rounded-xl border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 px-3.5 py-2.5">
          <MiniFlag color="amber" className="mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-amber-900 dark:text-amber-200">Queda pendiente de solucionar lo que indican el comentario u observaciones</p>
            <p className="text-xs text-amber-800/80 dark:text-amber-300/80">Lo dejó en ámbar {pend.read_by} el {fmtDateTime(pend.read_at)}</p>
          </div>
        </div>
      )}
      {comment && (
        <section className={`rounded-xl border p-3.5 ${comment.severity === 'amber'
          ? 'border-sky-300 bg-sky-50/70 dark:border-sky-800 dark:bg-sky-950/30'
          : 'border-slate-200 bg-slate-50/60 dark:border-slate-700 dark:bg-slate-900/30'}`}>
          <header className="flex flex-wrap items-center gap-2 mb-2">
            <MessageSquareText className="w-4 h-4 text-sky-600 dark:text-sky-400" />
            <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Comentario del cliente en el pedido</h4>
            <span className="ml-auto"><ReadChip issue={comment} /></span>
          </header>
          <div className="space-y-2">
            {msgs.map((m, k) => (
              <div key={k} className="rounded-lg bg-white dark:bg-slate-900/60 border border-sky-100 dark:border-sky-900/40 px-3 py-2">
                <p className="text-[15px] leading-relaxed text-slate-900 dark:text-white whitespace-pre-wrap break-words">{m.text}</p>
                {m.date && <p className="mt-1 text-[11px] text-slate-400">{fmtDateTime(m.date)}</p>}
              </div>
            ))}
          </div>
        </section>
      )}
      {note && (
        <section className={`rounded-xl border p-3.5 ${note.severity === 'amber'
          ? 'border-violet-300 bg-violet-50/70 dark:border-violet-800 dark:bg-violet-950/30'
          : 'border-slate-200 bg-slate-50/60 dark:border-slate-700 dark:bg-slate-900/30'}`}>
          <header className="flex flex-wrap items-center gap-2 mb-2">
            <StickyNote className="w-4 h-4 text-violet-600 dark:text-violet-400" />
            <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Observaciones del cliente (ficha)</h4>
            <span className="ml-auto"><ReadChip issue={note} /></span>
          </header>
          <NoteText note={String(note.data?.note || '')} orderId={orderId} />
        </section>
      )}
    </div>
  )
}

export interface ReaderItem {
  id: number
  reference?: string
  customer_name?: string
  total?: number
  inv: PrefacturaStatus
  /** Avisos de la parte de envío de la bandera (solo si no está en verde) */
  shippingIssues?: string[]
}

type OtherReason = { severity: 'red' | 'grey' | 'amber' | 'info'; msg: string }

/** Motivos de la bandera distintos del comentario/observaciones: avisos de factura y de envío. */
export function otherReasons(issues: { code: string; severity: string; msg: string }[], shipping?: ShippingValidation | string[]): OtherReason[] {
  const inv = issues.filter(i => !isMessageIssue(i) && i.severity !== 'info')
    .map(i => ({ severity: i.severity as OtherReason['severity'], msg: i.msg }))
  const ship = Array.isArray(shipping) ? shipping : shipping && shipping.status !== 'green' ? shipping.issues : []
  return [...inv, ...ship.map(m => ({ severity: 'amber' as const, msg: `Envío: ${m}` }))]
}

/**
 * Cuando el pedido tiene otros motivos para estar en ámbar (o rojo/en espera) no se pregunta el
 * estado: se marca el comentario/observaciones como leído y la bandera sigue por esos motivos.
 */
export function ReadAck({ reasons, onRead, pending, keys = false }: {
  reasons: OtherReason[]
  onRead: () => void
  pending: boolean
  keys?: boolean
}) {
  useEffect(() => {
    if (!keys) return
    const onKey = (e: KeyboardEvent) => {
      if (pending || (e.target as HTMLElement)?.tagName === 'INPUT') return
      const k = e.key.toLowerCase()
      if (k === 'enter' || k === 'l') { e.preventDefault(); onRead() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [keys, pending, onRead])
  const red = reasons.some(r => r.severity === 'red')
  return (
    <div className={`rounded-xl border-2 p-3.5 ${red
      ? 'border-red-200 bg-red-50/60 dark:border-red-900/60 dark:bg-red-950/20'
      : 'border-amber-200 bg-amber-50/60 dark:border-amber-900/60 dark:bg-amber-950/20'}`}>
      <div className="flex items-start gap-3">
        <span className={`relative inline-block w-4 h-5 mt-0.5 rounded-sm shrink-0 ${red ? 'bg-red-500' : 'bg-amber-500'}`}>
          <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[4px] border-l-transparent border-r-transparent border-b-white dark:border-b-slate-800" />
        </span>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${red ? 'text-red-800 dark:text-red-300' : 'text-amber-900 dark:text-amber-200'}`}>
            El pedido sigue en {red ? 'rojo' : 'ámbar'} por otros motivos
          </p>
          <ul className="mt-1 space-y-0.5">
            {reasons.slice(0, 5).map((r, k) => (
              <li key={k} className="flex items-start gap-1.5 text-xs text-slate-700 dark:text-slate-300">{SEV_ICON_DARK[r.severity]}<span>{r.msg}</span></li>
            ))}
            {reasons.length > 5 && <li className="text-xs text-slate-500">y {reasons.length - 5} más…</li>}
          </ul>
        </div>
        <button
          type="button"
          onClick={onRead}
          disabled={pending}
          className="self-center inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-900 dark:bg-slate-200 dark:hover:bg-white dark:text-slate-900 disabled:opacity-50 text-white text-sm font-semibold shadow-sm whitespace-nowrap"
        >
          {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Leído
          {keys && <kbd className="hidden sm:inline px-1.5 py-0.5 rounded border border-white/30 dark:border-slate-400 text-[10px] font-mono">Intro</kbd>}
        </button>
      </div>
    </div>
  )
}

/**
 * Lector rápido: recorre uno a uno los pedidos con comentario u observaciones sin leer.
 * Intro = leído y siguiente · ← → = anterior / siguiente · Esc = cerrar.
 */
export function MessagesReaderModal({ items, onClose }: { items: ReaderItem[]; onClose: () => void }) {
  const toast = useToast()
  const qc = useQueryClient()
  const [idx, setIdx] = useState(0)
  const [read, setRead] = useState<Record<number, PrefacturaStatus>>({})
  const cur = items[idx]
  const doneCount = Object.keys(read).length
  const finished = doneCount === items.length

  const nextUnread = useCallback((from: number, readNow: Record<number, PrefacturaStatus>) => {
    for (let k = 1; k <= items.length; k++) {
      const j = (from + k) % items.length
      if (!readNow[items[j].id]) return j
    }
    return from
  }, [items])

  const markM = useMutation({
    mutationFn: ({ id, decision }: { id: number; decision: Decision }) => prefactura.messagesRead(id, decision),
    onSuccess: (status, { id }) => {
      const now = { ...read, [id]: status }
      setRead(now)
      setIdx(nextUnread(idx, now))
      qc.invalidateQueries({ queryKey: ['prefactura-status'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'No se pudo marcar como leído'),
  })
  const undoM = useMutation({
    mutationFn: (id: number) => prefactura.messagesUnread(id),
    onSuccess: (_s, id) => {
      const now = { ...read }
      delete now[id]
      setRead(now)
      qc.invalidateQueries({ queryKey: ['prefactura-status'] })
    },
  })

  const isRead = cur ? !!read[cur.id] : false
  const decide = useCallback((decision: Decision) => {
    if (cur && !markM.isPending) markM.mutate({ id: cur.id, decision })
  }, [cur, markM])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === 'INPUT') return
      if (e.key === 'Escape') onClose()
      else if (finished) return
      else if (e.key === 'ArrowRight') setIdx(i => Math.min(items.length - 1, i + 1))
      else if (e.key === 'ArrowLeft') setIdx(i => Math.max(0, i - 1))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [items.length, onClose, finished])

  // Avisos del pedido: los del estado actualizado si ya se leyó (para enseñar "Leído por…")
  const issues = cur ? ((read[cur.id] || cur.inv).issues || []).filter(isMessageIssue) : []
  const others = cur ? otherReasons(cur.inv.issues || [], cur.shippingIssues) : []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-800 shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Cabecera + progreso */}
        <div className="px-5 pt-4 pb-3 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white">
          <div className="flex items-center gap-3">
            <MessageSquareText className="w-5 h-5" />
            <div className="flex-1">
              <h3 className="font-bold leading-tight">Comentarios y observaciones por leer</h3>
              <p className="text-xs text-white/85">Léelos y decide en qué estado queda cada pedido: verde o ámbar</p>
            </div>
            <span className="text-sm font-semibold tabular-nums">{doneCount} / {items.length} leídos</span>
            <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/20"><X className="w-5 h-5" /></button>
          </div>
          <div className="mt-3 h-1.5 rounded-full bg-white/30 overflow-hidden">
            <div className="h-full bg-white rounded-full transition-all duration-300" style={{ width: `${(doneCount / Math.max(1, items.length)) * 100}%` }} />
          </div>
        </div>

        {finished ? (
          <div className="flex flex-col items-center gap-3 py-16 px-6 text-center">
            <span className="p-4 rounded-2xl bg-green-50 dark:bg-green-900/20"><PartyPopper className="w-10 h-10 text-green-600" /></span>
            <p className="text-lg font-semibold text-slate-800 dark:text-white">¡Todo leído!</p>
            <p className="text-sm text-slate-500 max-w-sm">
              Has revisado los {items.length} pedido{items.length === 1 ? '' : 's'}
              {(() => {
                const amberN = Object.values(read).filter(st => (st.issues || []).some(i => i.data?.read?.decision === 'pendiente')).length
                return amberN ? `: ${amberN} quedan en ámbar para solucionarlos más tarde.` : '. Ya están en verde, listos para facturar.'
              })()}
            </p>
            <button onClick={onClose} className="mt-2 px-5 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium">Cerrar</button>
          </div>
        ) : cur && (
          <>
            {/* Pedido */}
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-5 py-3 border-b border-slate-100 dark:border-slate-700">
              <span className="text-lg font-bold text-slate-900 dark:text-white">Pedido {cur.id}</span>
              {cur.reference && <span className="font-mono text-xs text-slate-500">{cur.reference}</span>}
              {cur.customer_name && <span className="text-sm text-slate-700 dark:text-slate-200">{cur.customer_name}</span>}
              {typeof cur.total === 'number' && <span className="text-sm text-slate-500">{cur.total.toFixed(2)} €</span>}
              <span className="ml-auto text-xs text-slate-400 tabular-nums">{idx + 1} de {items.length}</span>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <CustomerMessages orderId={cur.id} issues={issues} />
              {!isRead && (others.length > 0
                ? <ReadAck key={cur.id} keys reasons={others} onRead={() => decide('ok')} pending={markM.isPending} />
                : <DecisionPicker key={cur.id} keys onDecide={decide} pending={markM.isPending} />)}
            </div>

            {/* Acciones */}
            <div className="flex items-center gap-2 px-5 py-3 border-t border-slate-100 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/30">
              <button onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-40">
                <ChevronLeft className="w-4 h-4" /> Anterior
              </button>
              <button onClick={() => setIdx(i => Math.min(items.length - 1, i + 1))} disabled={idx === items.length - 1}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-40">
                Saltar <ChevronRight className="w-4 h-4" />
              </button>
              <span className="hidden sm:inline ml-2 text-[11px] text-slate-400">{others.length > 0 ? 'Intro = leído' : 'V = verde · A = ámbar'} · ← → para moverte</span>
              <div className="ml-auto flex items-center gap-2">
                {isRead && (
                  <>
                    <button onClick={() => undoM.mutate(cur.id)} disabled={undoM.isPending}
                      className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700">
                      <Undo2 className="w-4 h-4" /> Deshacer
                    </button>
                    <button onClick={() => setIdx(nextUnread(idx, read))}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-200 text-sm font-medium">
                      Siguiente sin leer <ChevronRight className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const SEV_ICON_DARK = {
  red: <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />,
  grey: <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />,
  amber: <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />,
  info: <Info className="w-3.5 h-3.5 text-sky-500 shrink-0" />,
}

// ---------------------------------------------------------------------------------------------
// Modal de detalle
// ---------------------------------------------------------------------------------------------

const COLOR_LABEL: Record<string, { label: string; cls: string }> = {
  green: { label: 'Listo para facturar', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  amber: { label: 'Revisar', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  red: { label: 'No facturar', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  grey: { label: 'En espera', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
}

const MOTIVOS = [
  'Stock físico comprobado',
  'Comprobado en Factusol',
  'Dirección confirmada con el cliente',
  'NIF confirmado',
  'Importe ampliado correcto',
  'Otro',
]

const fmt = (v: any) => (v === null || v === undefined || v === '' ? '—' : typeof v === 'boolean' ? (v ? 'Sí' : 'No') : String(v))
const eur = (v: any) => (typeof v === 'number' ? `${v.toFixed(2)} €` : fmt(v))

const REGIMEN: Record<string, string> = { '0': 'Con IVA', '1': 'Sin IVA', '2': 'Intracomunitario', '3': 'Exportación' }
const VIES: Record<string, string> = {
  '7': 'Válido (VIES)', '11': 'Validado a mano', '4': 'INVÁLIDO', '5': 'VIES no disponible', '9': 'VIES bloqueado',
  '10': 'Error VIES', '8': 'Italia: sin verificar', '6': 'NIF español', '1': 'País no UE',
}

function CompareTable({ d }: { d: PrefacturaDetail }) {
  const c = d.compare || {}
  const rows: [string, string, string, string?][] = [
    ['Importe', `${eur(c.importe?.web)} cobrado${c.importe?.vale_dev ? ` + vale ${eur(c.importe.vale_dev)}` : ''}`, eur(c.importe?.factusol), `esperado ${eur(c.importe?.esperado)}`],
    ['IVA', c.iva?.web_cobro_iva ? 'Cobró IVA' : 'Sin IVA', REGIMEN[String(c.iva?.factusol_regimen)] || fmt(c.iva?.factusol_regimen),
      `ficha: ${REGIMEN[String(c.iva?.ficha_regimen)] || fmt(c.iva?.ficha_regimen)} · VIES: ${VIES[String(c.iva?.vies_codigo)] || 'sin registro'}`],
    ['NIF', fmt(c.nif?.web), fmt(c.nif?.factusol), `ficha: ${fmt(c.nif?.ficha)}`],
    ['Nombre', fmt(c.nombre?.web), fmt(c.nombre?.factusol), `ficha: ${fmt(c.nombre?.ficha)}`],
    ['Dirección', fmt(c.direccion?.web), fmt(c.direccion?.factusol)],
    ['CP / Población', `${fmt(c.cp?.web)} ${fmt(c.poblacion?.web)}`, `${fmt(c.cp?.factusol)} ${fmt(c.poblacion?.factusol)}`],
    ['Provincia', fmt(c.provincia?.web), fmt(c.provincia?.factusol)],
    ['País', `facturación ${fmt(c.pais?.facturacion)}`, '', `entrega ${fmt(c.pais?.entrega)}`],
    ['Estado web', fmt(c.estado_web?.nombre), ''],
    ['Forma de pago', fmt(c.forma_pago?.web), fmt(c.forma_pago?.factusol)],
  ]
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-slate-500 dark:text-slate-400 uppercase">
          <th className="text-left py-1.5 pr-2 font-medium w-28"></th>
          <th className="text-left py-1.5 pr-2 font-medium">PrestaShop</th>
          <th className="text-left py-1.5 pr-2 font-medium">Pedido Factusol</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
        {rows.map(([k, web, fs, extra]) => (
          <tr key={k} className="align-top">
            <td className="py-1.5 pr-2 font-medium text-slate-600 dark:text-slate-300">{k}</td>
            <td className="py-1.5 pr-2 text-slate-800 dark:text-slate-200 break-words">{web}</td>
            <td className="py-1.5 pr-2 text-slate-800 dark:text-slate-200 break-words">
              {fs}
              {extra && <div className="text-slate-400">{extra}</div>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const STOCK_CODES = new Set(['stock_ultima', 'stock_insuficiente', 'stock_competencia', 'stock_confirmado', 'stock_fisico_no'])

/** Recuento de almacén desde la bandera (si almacén no está): una respuesta por artículo para todos sus pedidos,
 *  o deshacer la respuesta. Solo en pedidos listos para preparar (data.ask = pendiente, data.count = contestado). */
function StockIssueActions({ data, onDone }: {
  data: Record<string, any>
  onDone: () => void
}) {
  const toast = useToast()
  const qc = useQueryClient()
  const [step, setStep] = useState<'ask' | 'mas' | 'menos'>('ask')
  const [more, setMore] = useState('')
  const demand = Number(data.demand) || 1
  const after = () => { onDone(); qc.invalidateQueries({ queryKey: ['stock-checks-pending'] }) }
  const answerM = useMutation({
    mutationFn: (v: { decision: StockDecision; real?: number }) => prefactura.stockCheckAnswer(data.art, v.decision, v.real),
    onSuccess: (r) => {
      toast.success(r.missing.length ? `Recuento guardado: ${r.missing.length} pedido(s) sin unidades en rojo` : 'Stock confirmado')
      setStep('ask'); after()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'No se pudo guardar'),
  })
  const undoM = useMutation({
    mutationFn: () => prefactura.stockCheckUndo(data.art),
    onSuccess: () => { toast.success('Recuento de almacén deshecho'); after() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'No se pudo deshacer'),
  })
  const btn = 'inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg font-medium disabled:opacity-50'

  if (data.count) {
    return (
      <button onClick={() => undoM.mutate()} disabled={undoM.isPending}
        className={`${btn} ml-2 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 align-middle`}>
        <Undo2 className="w-3 h-3" /> Deshacer recuento
      </button>
    )
  }
  if (!data.ask) return null
  const moreN = Number(more)
  return (
    <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
      {step === 'ask' ? (
        <>
          <span className="text-xs text-slate-500">¿Hay {demand} en el almacén?</span>
          <button onClick={() => answerM.mutate({ decision: 'ok' })} disabled={answerM.isPending}
            className={`${btn} bg-emerald-500 hover:bg-emerald-600 text-white`}>
            <Check className="w-3 h-3" /> Sí, están
          </button>
          <button onClick={() => { setMore(String(Math.floor(demand) + 1)); setStep('mas') }} disabled={answerM.isPending}
            className={`${btn} bg-sky-500 hover:bg-sky-600 text-white`}>
            Hay más
          </button>
          <button onClick={() => setStep('menos')} disabled={answerM.isPending}
            className={`${btn} bg-rose-500 hover:bg-rose-600 text-white`}>
            <X className="w-3 h-3" /> No llegan
          </button>
        </>
      ) : step === 'mas' ? (
        <>
          <span className="text-xs text-slate-500">¿Cuántas hay en total?</span>
          <input type="number" min={Math.floor(demand) + 1} value={more} onChange={(e) => setMore(e.target.value)}
            className="w-16 px-2 py-0.5 text-xs rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700" />
          <button onClick={() => answerM.mutate({ decision: 'mas', real: moreN })} disabled={answerM.isPending || !(moreN > demand)}
            className={`${btn} bg-sky-500 hover:bg-sky-600 text-white`}>
            Guardar
          </button>
          <button onClick={() => setStep('ask')} className="text-xs text-slate-500 hover:text-slate-700">Cancelar</button>
        </>
      ) : (
        <>
          <span className="text-xs text-slate-500">¿Cuántas hay realmente?</span>
          {Array.from({ length: Math.min(Math.ceil(demand), 8) }, (_, k) => k).map(k => (
            <button key={k} onClick={() => answerM.mutate({ decision: 'menos', real: k })} disabled={answerM.isPending}
              className={`${btn} min-w-7 justify-center border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:bg-rose-900/20 dark:border-rose-900 dark:text-rose-300`}>
              {k}
            </button>
          ))}
          <button onClick={() => setStep('ask')} className="text-xs text-slate-500 hover:text-slate-700">Cancelar</button>
        </>
      )}
    </span>
  )
}

export function InvoiceCheckModal({ orderId, reference, shipping, onClose }: {
  orderId: number
  reference?: string
  /** Parte de envío de la bandera: si tiene avisos, cuenta como otro motivo de ámbar */
  shipping?: ShippingValidation
  onClose: () => void
}) {
  const toast = useToast()
  const qc = useQueryClient()
  const [motivo, setMotivo] = useState(MOTIVOS[0])
  const [nota, setNota] = useState('')

  const q = useQuery({
    queryKey: ['prefactura-detail', orderId],
    queryFn: () => prefactura.detail(orderId),
    retry: 0,
  })

  const done = () => {
    qc.invalidateQueries({ queryKey: ['prefactura-status'] })
    qc.invalidateQueries({ queryKey: ['prefactura-detail', orderId] })
  }

  const reviewM = useMutation({
    mutationFn: () => prefactura.review(orderId, motivo, nota),
    onSuccess: () => { toast.success('Marcado como revisado'); done() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'No se pudo marcar como revisado'),
  })
  const unreviewM = useMutation({
    mutationFn: () => prefactura.unreview(orderId),
    onSuccess: () => { toast.success('Revisión deshecha'); done() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'No se pudo deshacer'),
  })

  const [changing, setChanging] = useState(false)
  const readM = useMutation({
    mutationFn: ({ decision }: { decision: Decision }) => prefactura.messagesRead(orderId, decision),
    onSuccess: (_s, v) => { toast.success(v.decision === 'ok' ? 'Leído: pedido en verde' : 'Leído: queda en ámbar'); setChanging(false); done() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'No se pudo marcar como leído'),
  })
  const unreadM = useMutation({
    mutationFn: () => prefactura.messagesUnread(orderId),
    onSuccess: () => done(),
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'No se pudo deshacer'),
  })

  const d = q.data
  const allIssues = (d?.issues || [])
  const msgIssues = allIssues.filter(isMessageIssue)
  const issues = allIssues.filter(i => !isMessageIssue(i))
  const msgUnread = msgIssues.some(i => i.severity === 'amber' && !i.data?.read)
  // La revisión con motivo es para los demás avisos; el comentario/observaciones se marcan con "Leído"
  const canReview = !!d && d.color === 'amber' && issues.some(i => i.severity === 'amber')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={(e) => { e.stopPropagation(); onClose() }}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-slate-900 dark:text-white">Facturación del pedido {reference || `#${orderId}`}</h3>
            {d && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${COLOR_LABEL[d.color]?.cls}`}>
                {d.reviewed ? 'Revisado · listo' : COLOR_LABEL[d.color]?.label}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="p-5 space-y-5">
          {q.isLoading ? (
            <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
          ) : q.isError || !d ? (
            <p className="text-sm text-slate-500">
              {(q.error as any)?.response?.data?.detail || 'No se pudo cargar el detalle (el pedido puede haberse facturado ya).'}
            </p>
          ) : (
            <>
              {msgIssues.length > 0 && (
                <div className="space-y-3">
                  <CustomerMessages orderId={orderId} issues={msgIssues} />
                  {(msgUnread || changing) && otherReasons(issues, shipping).length > 0 ? (
                    <ReadAck reasons={otherReasons(issues, shipping)} onRead={() => readM.mutate({ decision: 'ok' })} pending={readM.isPending} />
                  ) : msgUnread || changing ? (
                    <DecisionPicker onDecide={(decision) => readM.mutate({ decision })} pending={readM.isPending} />
                  ) : (
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setChanging(true)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                      >
                        Cambiar estado
                      </button>
                      <button
                        onClick={() => unreadM.mutate()}
                        disabled={unreadM.isPending}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                      >
                        <Undo2 className="w-3.5 h-3.5" /> Volver a no leído
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div>
                <p className="text-xs font-medium uppercase text-slate-500 mb-2">Avisos</p>
                {issues.length === 0 ? (
                  <p className="text-sm text-green-700 dark:text-green-400 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> {msgUnread ? 'Sin más avisos: si lo dejas en verde queda listo para facturar' : 'Todo correcto: listo para facturar'}</p>
                ) : (
                  <ul className="space-y-1.5">
                    {issues.map((i, k) => (
                      <li key={k} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                        <span className="mt-0.5">{{
                          red: <XCircle className="w-4 h-4 text-red-500" />,
                          grey: <Clock className="w-4 h-4 text-slate-400" />,
                          amber: <AlertTriangle className="w-4 h-4 text-amber-500" />,
                          info: <Info className="w-4 h-4 text-sky-500" />,
                        }[i.severity]}</span>
                        <span className="flex-1">
                          {i.msg}
                          {STOCK_CODES.has(i.code) && i.data?.art && (
                            <StockIssueActions data={i.data} onDone={done} />
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <p className="text-xs font-medium uppercase text-slate-500 mb-2">PrestaShop vs Factusol</p>
                <CompareTable d={d} />
              </div>

              {d.reviewed && d.review ? (
                <div className="flex items-center justify-between rounded-lg bg-green-50 dark:bg-green-900/20 px-4 py-3 text-sm">
                  <div className="text-green-800 dark:text-green-300">
                    Revisado por <b>{d.review.reviewed_by}</b> el {d.review.reviewed_at?.replace('T', ' ')} — {d.review.motivo}
                    {d.review.nota ? <span className="text-green-700/80"> · {d.review.nota}</span> : null}
                  </div>
                  <button
                    onClick={() => unreviewM.mutate()}
                    disabled={unreviewM.isPending}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-green-300 text-green-800 hover:bg-green-100 dark:text-green-300 dark:border-green-800"
                  >
                    <Undo2 className="w-3.5 h-3.5" /> Deshacer revisión
                  </button>
                </div>
              ) : canReview ? (
                <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/60 dark:bg-amber-900/10 p-4 space-y-3">
                  {d.review_expired && (
                    <p className="text-xs text-amber-800 dark:text-amber-300">Había una revisión anterior, pero los avisos han cambiado: hay que volver a revisarlo.</p>
                  )}
                  <p className="text-sm text-slate-700 dark:text-slate-200">Si has comprobado los avisos y está bien para facturar, márcalo como revisado (vale mientras los avisos no cambien).</p>
                  <div className="flex flex-wrap gap-2">
                    <select value={motivo} onChange={(e) => setMotivo(e.target.value)}
                      className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm">
                      {MOTIVOS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Nota (opcional)"
                      className="flex-1 min-w-48 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm" />
                    <button
                      onClick={() => reviewM.mutate()}
                      disabled={reviewM.isPending || (motivo === 'Otro' && !nota.trim())}
                      className="px-3 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-sm font-medium"
                    >
                      Marcar revisado
                    </button>
                  </div>
                </div>
              ) : d.color === 'red' ? (
                <p className="text-xs text-slate-500">Los avisos en rojo no se pueden marcar como revisados: hay que corregirlos (en Factusol o en la web) y pulsar Actualizar.</p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
